import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Spinner, Alert, Dropdown, Modal, Button } from 'react-bootstrap';
import useAffiliations from 'hooks/userManagement/useAffiliations';
import useFormattedDate from 'hooks/useFormatDate';
import SubtleBadge from 'components/common/SubtleBadge';
import { toast } from 'react-toastify';
import { Empty } from 'antd';

const UserAffiliationAssignmentsTable = ({ userId }) => {
  const { 
    justFetchUserAffiliations, 
    loading, 
    removeAffiliationFromUser, 
    updateAssignment,
    getAssignment 
  } = useAffiliations();
  const [assignments, setAssignments] = useState([]);
  const [error, setError] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { formatDateTime } = useFormattedDate();

  const fetchAssignments = useCallback(async () => {
    if (!userId) return;
    try {
      setError(null);
      const data = await justFetchUserAffiliations(userId);
      setAssignments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch user affiliation assignments:', err);
      setError(err.message || 'Failed to load affiliation assignments');
    }
  }, [userId, justFetchUserAffiliations]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'VERIFIED':
        return 'success';
      case 'SELF_DECLARED':
        return 'warning';
      case 'REVOKED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getVisibilityColor = (visibility) => {
    switch (visibility) {
      case 'PUBLIC':
        return 'success';
      case 'HELPERS_ONLY':
        return 'info';
      case 'PRIVATE':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const handleView = async (assignment) => {
    try {

      const fullAssignment = await getAssignment(userId, assignment.id);
      setSelectedAssignment(fullAssignment);
      setShowViewModal(true);
    } catch (err) {
      console.error('Failed to fetch assignment details:', err);
      toast.error('Failed to load assignment details');
      // Still show the assignment we have
      setSelectedAssignment(assignment);
      setShowViewModal(true);
    }
  };

  const handleDeleteClick = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAssignment || !userId) return;
    
    setIsProcessing(true);
    try {
      await removeAffiliationFromUser(userId, selectedAssignment.id);
      toast.success('Affiliation assignment deleted successfully');
      setShowDeleteModal(false);
      setSelectedAssignment(null);
      await fetchAssignments();
    } catch (err) {
      console.error('Failed to delete assignment:', err);
      toast.error(err.message || 'Failed to delete affiliation assignment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerify = async (assignment) => {
    if (!userId || !assignment.id) return;
    
    setIsProcessing(true);
    try {
      const updatePayload = {
        ...assignment,
        status: 'VERIFIED',
        verifiedBy: 'admin',
        verifiedAt: new Date().toISOString()
      };
      
      await updateAssignment(userId, assignment.id, updatePayload);
      toast.success('Affiliation assignment verified successfully');
      await fetchAssignments();
    } catch (err) {
      console.error('Failed to verify assignment:', err);
      toast.error(err.message || 'Failed to verify affiliation assignment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedAssignment(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedAssignment(null);
  };

  if (loading) {
    return (
      <Card className="custom-shadow">
        <Card.Header>
          <h6 className="mb-0 fw-bold">Assigned Affiliations</h6>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" variant="success" />
          <span className="ms-2">Loading affiliations...</span>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="custom-shadow">
        <Card.Header>
          <h6 className="mb-0 fw-bold">Assigned Affiliations</h6>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="custom-shadow">
      <Card.Header>
        <h6 className="mb-0 fw-bold">Assigned Affiliations</h6>
      </Card.Header>
      <Card.Body>
        {assignments.length === 0 ? (
          <div className="text-center pb-4 text-muted">
            <Empty
              description="No affiliation assignments found for this user."
              style={{ fontFamily: "Inter", fontSize: 13 }}
            />
          </div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="mb-0 fs-10">
              <thead className="bg-light">
                <tr>
                  <th className="text-nowrap">ID</th>
                  <th className="text-nowrap">Affiliation ID</th>
                  <th className="text-nowrap">Status</th>
                  <th className="text-nowrap">Verified By</th>
                  <th className="text-nowrap">Verified At</th>
                  <th className="text-nowrap">Visibility</th>
                  <th className="text-nowrap text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <tr key={assignment.id}>
                    <td className="text-nowrap">{assignment.id || "N/A"}</td>
                    <td className="text-nowrap">
                      {assignment.affiliationId || "N/A"}
                    </td>
                    <td className="text-nowrap">
                      <SubtleBadge pill bg={getStatusColor(assignment.status)}>
                        {assignment.status || "N/A"}
                      </SubtleBadge>
                    </td>
                    <td className="text-nowrap">
                      {assignment.verifiedBy || "N/A"}
                    </td>
                    <td className="text-nowrap">
                      {assignment.verifiedAt
                        ? formatDateTime(assignment.verifiedAt)
                        : "N/A"}
                    </td>
                    <td className="text-nowrap">
                      <SubtleBadge
                        pill
                        bg={getVisibilityColor(assignment.visibility)}
                      >
                        {assignment.visibility || "N/A"}
                      </SubtleBadge>
                    </td>
                    <td className="text-nowrap text-center">
                      <Dropdown className="action-dropdown p-0">
                        <Dropdown.Toggle
                          as="button"
                          className="bg-transparent border-0 p-0 shadow-none text-secondary"
                          disabled={isProcessing}
                        >
                          ⋯
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="action-menu">
                          <Dropdown.Item onClick={() => handleView(assignment)}>
                            View
                          </Dropdown.Item>
                          {assignment.status !== "VERIFIED" && (
                            <Dropdown.Item
                              onClick={() => handleVerify(assignment)}
                              disabled={isProcessing}
                            >
                              Verify
                            </Dropdown.Item>
                          )}
                          <Dropdown.Divider />
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => handleDeleteClick(assignment)}
                            disabled={isProcessing}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>

      {/* View Modal */}
      <Modal
        show={showViewModal}
        onHide={handleCloseViewModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold">
            Affiliation Assignment Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedAssignment && (
            <Table borderless className="fs-10">
              <tbody>
                <tr>
                  <td
                    className="py-2 fw-bold text-nowrap"
                    style={{ width: "180px" }}
                  >
                    ID:
                  </td>
                  <td className="py-2">{selectedAssignment.id || "N/A"}</td>
                </tr>
                <tr>
                  <td className="py-2 fw-bold text-nowrap">User ID:</td>
                  <td className="py-2">{selectedAssignment.userId || "N/A"}</td>
                </tr>
                <tr>
                  <td className="py-2 fw-bold text-nowrap">Affiliation ID:</td>
                  <td className="py-2">{selectedAssignment.affiliationId || "N/A"}</td>
                </tr>
                <tr>
                  <td className="py-2 fw-bold text-nowrap">Status:</td>
                  <td className="py-2">
                    <SubtleBadge
                      pill
                      bg={getStatusColor(selectedAssignment.status)}
                    >
                      {selectedAssignment.status || "N/A"}
                    </SubtleBadge>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 fw-bold text-nowrap">Verified By:</td>
                  <td className="py-2">
                    {selectedAssignment.verifiedBy || "N/A"}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 fw-bold text-nowrap">Verified At:</td>
                  <td className="py-2">
                    {selectedAssignment.verifiedAt
                      ? formatDateTime(selectedAssignment.verifiedAt)
                      : "N/A"}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 fw-bold text-nowrap">Visibility:</td>
                  <td className="py-2">
                    <SubtleBadge
                      pill
                      bg={getVisibilityColor(selectedAssignment.visibility)}
                    >
                      {selectedAssignment.visibility || "N/A"}
                    </SubtleBadge>
                  </td>
                </tr>
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this affiliation assignment? This
          action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseDeleteModal}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default UserAffiliationAssignmentsTable;

